import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, Clock } from "lucide-react";
import { toast } from "sonner";
import useReveal from "../hooks/useReveal";
import { useContent } from "../context/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [ref, inView] = useReveal();
  const { content } = useContent();
  const contact = content.settings.contact;
  const hours = content.settings.hours || [];

  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! Your message has been sent.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Sorry, something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#D8CCB8] focus:border-[#C6A15B] outline-none py-3 text-[#1A1A1A] placeholder-[#A89F8E] transition-colors";

  return (
    <section id="contact" className="bg-[#F7F2EA] py-24 md:py-32 px-6">
      <div ref={ref} className={`max-w-6xl mx-auto reveal ${inView ? "in-view" : ""}`}>
        <div className="flex items-center gap-4 mb-3">
          <span className="text-xs tracking-[0.25em] text-[#C6A15B] uppercase">/ 06</span>
          <span className="gold-divider" />
          <span className="text-xs tracking-[0.25em] text-[#8A857A] uppercase">Let's Get in Touch</span>
        </div>

        <h2 className="font-serif-display text-4xl md:text-6xl text-[#1A1A1A] mb-14">Say Hello</h2>

        <div className="grid md:grid-cols-2 gap-14">
          <div>
            <p className="text-[#4A463F] font-light leading-relaxed mb-10">
              Have questions or feedback? We're here to help. Reach us via phone, email, or the form
              and our team will assist you promptly and personally.
            </p>

            <ul className="space-y-6">
              <li className="flex items-center gap-4">
                <Phone className="text-[#C6A15B]" size={20} strokeWidth={1.5} />
                <a href={`tel:${(contact.phone || "").replace(/[^0-9]/g, "")}`} className="text-[#1A1A1A] hover:text-[#C6A15B] transition-colors">
                  {contact.phone}
                </a>
              </li>
              <li className="flex items-center gap-4">
                <Mail className="text-[#C6A15B]" size={20} strokeWidth={1.5} />
                <a href={`mailto:${contact.email}`} className="text-[#1A1A1A] hover:text-[#C6A15B] transition-colors">
                  {contact.email}
                </a>
              </li>
            </ul>

            <div className="mt-10 pt-10 border-t border-[#E4DBCB]">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="text-[#C6A15B]" size={18} strokeWidth={1.5} />
                <span className="text-xs tracking-[0.2em] uppercase text-[#8A857A]">Hours</span>
              </div>
              <ul className="space-y-2">
                {hours.map((h, i) => (
                  <li key={i} className="flex justify-between text-sm text-[#4A463F]">
                    <span>{h.day}</span>
                    <span className="text-[#1A1A1A]">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <input name="name" value={form.name} onChange={handleChange} placeholder="Your Name *" className={inputCls} />
            <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="Your Email *" className={inputCls} />
            <input name="subject" value={form.subject} onChange={handleChange} placeholder="Subject" className={inputCls} />
            <textarea name="message" value={form.message} onChange={handleChange} placeholder="Your Message *" rows={5} className={inputCls + " resize-none"} />
            <button
              type="submit"
              disabled={submitting}
              className="bg-[#0C0C0C] text-[#C6A15B] text-xs tracking-[0.2em] uppercase px-9 py-4 hover:bg-[#1a1a1a] transition-colors disabled:opacity-60"
            >
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
